// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, Phone, Star, ShoppingBag, ChevronRight, LogOut, CheckCircle, Edit2, Save, X } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

function statusColor(status) {
  const map = {
    PENDING: '#F59E0B', CONFIRMED: '#3B82F6', PREPARING: '#8B5CF6',
    READY: '#16A34A', OUT_FOR_DELIVERY: '#F97316',
    DELIVERED: '#16A34A', PICKED_UP: '#16A34A', CANCELLED: '#EF4444',
  };
  return map[status] || '#94A3B8';
}

function statusLabel(status) {
  const map = {
    PENDING: 'Pending', CONFIRMED: 'Confirmed', PREPARING: 'Preparing',
    READY: 'Ready', OUT_FOR_DELIVERY: 'Out for Delivery',
    DELIVERED: 'Delivered', PICKED_UP: 'Picked Up', CANCELLED: 'Cancelled',
  };
  return map[status] || status;
}

export default function AccountPage() {
  const { user, customer, signOut, refreshCustomer } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState('');

  const [form, setForm] = useState({
    full_name: customer?.full_name || '',
    phone:     customer?.phone     || '',
  });

  useEffect(() => {
    if (customer) {
      setForm({ full_name: customer.full_name || '', phone: customer.phone || '' });
      fetchOrders();
    }
  }, [customer]);

  const fetchOrders = async () => {
    const { data } = await supabase
      .from('grocery_orders')
      .select('id, order_number, order_status, total_cents, loyalty_points_earned, created_at, fulfillment_type, grocery_order_items(id)')
      .eq('customer_id', customer.id)
      .order('created_at', { ascending: false })
      .limit(5);
    setOrders(data || []);
    setLoading(false);
  };

  const handleSave = async () => {
    if (!form.full_name.trim()) return;
    setSaving(true);
    const { error } = await supabase
      .from('grocery_store_customers')
      .update({ full_name: form.full_name.trim(), phone: form.phone.trim() || null })
      .eq('id', customer.id);
    if (!error) {
      await refreshCustomer();
      setEditing(false);
      setSaveMsg('Profile updated!');
      setTimeout(() => setSaveMsg(''), 3000);
    }
    setSaving(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const tier       = customer?.grocery_loyalty_tiers;
  const points     = customer?.loyalty_points || 0;
  const totalSpent = orders.reduce((sum, o) => sum + (o.total_cents || 0), 0);

  const tierColorMap = {
    'Bronze':   { from: '#CD7F32', to: '#A0522D' },
    'Silver':   { from: '#94A3B8', to: '#64748B' },
    'Gold':     { from: '#F59E0B', to: '#D97706' },
    'Platinum': { from: '#8B5CF6', to: '#6D28D9' },
  };
  const tierColors = tier ? (tierColorMap[tier.tier_name] || { from: '#94A3B8', to: '#64748B' }) : null;

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page-container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <h1 style={{ fontSize: 26, fontWeight: 900, color: '#14532D', marginBottom: 28 }}>My Account</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 28, alignItems: 'start' }}>
        {/* Main column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Profile card */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: '#14532D', margin: 0 }}>Profile</h2>
              {!editing ? (
                <button className="btn btn-ghost btn-sm" style={{ color: '#16A34A', display: 'flex', alignItems: 'center', gap: 6 }} onClick={() => setEditing(true)}>
                  <Edit2 size={14} /> Edit
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-ghost btn-sm" style={{ color: '#94A3B8' }} onClick={() => { setEditing(false); setForm({ full_name: customer?.full_name || '', phone: customer?.phone || '' }); }}>
                    <X size={14} />
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Save size={14} /> {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {saveMsg && (
              <div style={{ background: '#DCFCE7', color: '#16A34A', fontSize: 13, fontWeight: 600, padding: '8px 12px', borderRadius: 8, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckCircle size={14} /> {saveMsg}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Avatar */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #16A34A, #14532D)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, color: '#fff', fontWeight: 900, flexShrink: 0,
                }}>
                  {(customer?.full_name || user?.email || '?')[0].toUpperCase()}
                </div>
                <div>
                  {editing ? (
                    <input
                      className="input"
                      style={{ minHeight: 38, fontSize: 14, fontWeight: 700 }}
                      value={form.full_name}
                      onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
                      placeholder="Your full name"
                    />
                  ) : (
                    <>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#14532D' }}>{customer?.full_name || 'Guest'}</div>
                      {tier && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: `linear-gradient(135deg, ${tierColors.from}, ${tierColors.to})`, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 12, marginTop: 4 }}>
                          {tier.tier_icon} {tier.tier_name} Member
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Email (read-only) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#F8FAFC', borderRadius: 10 }}>
                <Mail size={15} color="#94A3B8" />
                <div>
                  <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Email</div>
                  <div style={{ fontSize: 14, color: '#374151' }}>{user?.email}</div>
                </div>
              </div>

              {/* Phone */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: editing ? 0 : '10px 14px', background: editing ? 'transparent' : '#F8FAFC', borderRadius: 10 }}>
                {!editing && <Phone size={15} color="#94A3B8" />}
                {editing ? (
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>Phone</label>
                    <input
                      className="input"
                      style={{ minHeight: 38, fontSize: 14 }}
                      value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="(555) 000-0000"
                      type="tel"
                    />
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Phone</div>
                    <div style={{ fontSize: 14, color: customer?.phone ? '#374151' : '#CBD5E1' }}>
                      {customer?.phone || 'Not set'}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Orders */}
          <div className="card" style={{ padding: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: '#14532D', margin: 0 }}>Recent Orders</h2>
              <Link to="/orders" style={{ textDecoration: 'none' }}>
                <button className="btn btn-ghost btn-sm" style={{ color: '#16A34A' }}>
                  View All <ChevronRight size={14} />
                </button>
              </Link>
            </div>

            {orders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#94A3B8' }}>
                <ShoppingBag size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                <div style={{ fontSize: 14 }}>No orders yet.</div>
              </div>
            ) : (
              orders.map(order => {
                const color = statusColor(order.order_status);
                return (
                  <Link key={order.id} to={`/orders/${order.id}`} style={{ textDecoration: 'none' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 0', borderBottom: '1px solid #F0FDF4', cursor: 'pointer',
                    }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#14532D' }}>Order #{order.order_number}</div>
                        <div style={{ fontSize: 12, color: '#94A3B8' }}>
                          {new Date(order.created_at).toLocaleDateString()} · {order.grocery_order_items?.length || 0} items
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: '#14532D' }}>{fmt(order.total_cents)}</div>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700, background: color + '18', color }}>
                          {statusLabel(order.order_status)}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {/* Danger zone */}
          <div className="card" style={{ padding: 20, border: '1px solid #FEE2E2' }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: '#DC2626', marginBottom: 12 }}>Account Actions</h2>
            <button
              className="btn btn-sm"
              style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA', display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={handleSignOut}
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        {/* Loyalty sidebar */}
        <div style={{ position: 'sticky', top: 80 }}>
          {/* Loyalty summary */}
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
            {tier && tierColors && (
              <div style={{ background: `linear-gradient(135deg, ${tierColors.from}, ${tierColors.to})`, padding: '24px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 4 }}>{tier.tier_icon}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#fff' }}>{tier.tier_name} Member</div>
                <div style={{ fontSize: 28, fontWeight: 900, color: '#fff', marginTop: 8 }}>{points.toLocaleString()}</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>loyalty points</div>
              </div>
            )}
            <div style={{ padding: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#64748B' }}>Total Spent</span>
                  <span style={{ fontWeight: 700, color: '#14532D' }}>{fmt(totalSpent)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: '#64748B' }}>Total Orders</span>
                  <span style={{ fontWeight: 700, color: '#14532D' }}>{orders.length}</span>
                </div>
                {tier?.discount_percent > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#64748B' }}>Member Discount</span>
                    <span style={{ fontWeight: 700, color: '#16A34A' }}>{tier.discount_percent}% off</span>
                  </div>
                )}
                {tier?.point_multiplier > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#64748B' }}>Points Multiplier</span>
                    <span style={{ fontWeight: 700, color: '#16A34A' }}>{tier.point_multiplier}×</span>
                  </div>
                )}
              </div>
              <Link to="/loyalty" style={{ textDecoration: 'none' }}>
                <button className="btn btn-outline btn-sm" style={{ width: '100%', marginTop: 14, color: '#16A34A', borderColor: '#BBF7D0' }}>
                  <Star size={14} /> View Loyalty Rewards
                </button>
              </Link>
            </div>
          </div>

          {/* Quick links */}
          <div className="card" style={{ padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Quick Links</div>
            {[
              { to: '/orders',   icon: <ShoppingBag size={14} />, label: 'Order History' },
              { to: '/loyalty',  icon: <Star size={14} />,        label: 'Loyalty & Rewards' },
              { to: '/coupons',  icon: '🏷️',                      label: 'Digital Coupons' },
              { to: '/products', icon: '🛍️',                      label: 'Shop Groceries' },
            ].map(link => (
              <Link key={link.to} to={link.to} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 10px', borderRadius: 8, cursor: 'pointer',
                  color: '#374151', fontSize: 13, fontWeight: 500,
                  transition: 'background 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: '#16A34A' }}>{link.icon}</span>
                    {link.label}
                  </div>
                  <ChevronRight size={13} color="#CBD5E1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
